import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

import { useQueryClient } from '@tanstack/react-query'
import { CalendarIcon } from 'lucide-react'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DELIVERABLE_SET_DETAIL_QUERY_KEY,
  DELIVERABLE_SET_LIST_QUERY_KEY,
  useDeliverableSetsStore,
  useDraftApprovalRequest,
  useSendToReview,
} from '@/features/deliverable-set'
import { ERoleType } from '@/features/user-management'
import type {
  RequestApprovalDeliverableSet,
  RequestDraftApprovalDeliverableSet,
  User,
} from '@/schema'
import { inviteUsersRedirectUrl } from '@/utils/common'

import {
  buildApprovalUserInvitation,
  buildInviteUserDeliverableSet,
} from '../utils'

import { ErrorMessage } from './ErrorMessage'
import { InviteReviewerField } from './InviteReviewerField'
import { NoteField } from './NoteField'

export interface SendToReviewFormData {
  targetDate: Date | null
  note?: string
}

export interface UserWithFullName extends User {
  fullName: string
}

const SendToReviewDialog: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const {
    showSendApprovalDialog,
    requestApprovalDeliverableSet,
    inviteUsers,
    setSendToReviewDialogOpen,
    clearSendToReviewDeliverableSet,
    setInviteUsers,
  } = useDeliverableSetsStore()

  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const mutation = useSendToReview()

  const { mutate: draftApprovalRequest } = useDraftApprovalRequest()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    setValue,
    watch,
    getValues,
  } = useForm<SendToReviewFormData>({
    defaultValues: {
      targetDate: null,
      note: '',
    },
    mode: 'onChange',
  })

  const targetDateWatch = watch('targetDate')
  const noteWatch = watch('note')

  const isMandatoryFieldsEmpty = useMemo(() => {
    return !inviteUsers?.length || !targetDateWatch
  }, [inviteUsers, targetDateWatch])

  // Check if the invite users have a reviewer role
  const isHaveReviewer = useMemo(
    () => inviteUsers?.find((u) => u.roleName === ERoleType.REVIEWER),
    [inviteUsers]
  )

  const isSaving = useMemo(() => mutation.isPending, [mutation.isPending])

  useEffect(() => {
    if (!showSendApprovalDialog) return

    // Set draft approval if it is set
    if (!requestApprovalDeliverableSet?.draft_approval) return

    const draftApproval = requestApprovalDeliverableSet.draft_approval

    // Set due date
    if (draftApproval.due_date) {
      setValue('targetDate', dayjs(draftApproval.due_date).toDate())
    }

    // Set note
    if (draftApproval.note) setValue('note', draftApproval.note)

    // Set reviewers
    if (draftApproval.reviewers) {
      setInviteUsers(buildInviteUserDeliverableSet(draftApproval.reviewers))
    }
  }, [
    requestApprovalDeliverableSet?.draft_approval,
    setInviteUsers,
    setValue,
    showSendApprovalDialog,
  ])

  useEffect(() => {
    if (!showSendApprovalDialog) {
      reset({ targetDate: null, note: '' })
      mutation.reset()
      setErrorMsg(null)
      clearSendToReviewDeliverableSet()
      setInviteUsers([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSendApprovalDialog, reset])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setSendToReviewDialogOpen(open)
      if (!open) {
        clearSendToReviewDeliverableSet()
      }
    },
    [setSendToReviewDialogOpen, clearSendToReviewDeliverableSet]
  )

  /**
   * @summary Draft approval request for the deliverable set
   */
  const handleDraftApprovalRequest = useCallback(() => {
    const data = getValues()
    if (!requestApprovalDeliverableSet?.id) {
      setErrorMsg(t('deliverableSet.dialog.messages.errorGeneric'))
      return
    }

    // If all fields are empty, return early
    if (!data.targetDate && !data.note && !inviteUsers?.length) {
      return
    }

    const payload: Partial<RequestDraftApprovalDeliverableSet> = {}
    // Add due date if it is set
    if (data.targetDate) {
      payload.due_date = dayjs(data.targetDate).endOf('day').utc().toISOString()
    }

    // Add note if it is set
    if (data.note) payload.note = data.note

    // Add reviewers if there are any
    if (inviteUsers?.length) {
      payload.reviewers = buildApprovalUserInvitation(
        inviteUsers,
        requestApprovalDeliverableSet?.id
      )
    }

    draftApprovalRequest(
      {
        deliverableSetId: requestApprovalDeliverableSet.id,
        data: payload as RequestDraftApprovalDeliverableSet,
      },
      {
        onSuccess: () => {
          triggerToast(
            'success',
            t('deliverableSet.draftApprovalRequest.messages.success')
          )

          queryClient.invalidateQueries({
            queryKey: DELIVERABLE_SET_DETAIL_QUERY_KEY(
              requestApprovalDeliverableSet.id
            ),
          })

          queryClient.invalidateQueries({
            queryKey: DELIVERABLE_SET_LIST_QUERY_KEY([]),
          })

          handleOpenChange(false)
        },
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<{
            error?: { message?: string }
            message?: string
          }>
          const errorMessage =
            axiosError.response?.data?.error?.message ||
            axiosError.response?.data?.message ||
            axiosError.message ||
            t('deliverableSet.dialog.messages.errorGeneric')

          setErrorMsg(errorMessage)
        },
      }
    )
  }, [
    draftApprovalRequest,
    getValues,
    handleOpenChange,
    inviteUsers,
    queryClient,
    requestApprovalDeliverableSet?.id,
    t,
  ])

  const onSubmit = useCallback(
    (data: SendToReviewFormData) => {
      setErrorMsg(null)

      if (!requestApprovalDeliverableSet?.id) {
        setErrorMsg(t('deliverableSet.dialog.messages.errorGeneric'))
        return
      }

      const payload: RequestApprovalDeliverableSet = {
        due_date: dayjs(data.targetDate).endOf('day').utc().toISOString(),
        owner_id: requestApprovalDeliverableSet?.owner_id || '',
        note: data?.note,
        redirect_url: inviteUsersRedirectUrl(),
        reviewers: buildApprovalUserInvitation(
          inviteUsers,
          requestApprovalDeliverableSet?.id
        ),
      }

      mutation.mutate(
        { id: requestApprovalDeliverableSet.id, data: payload },
        {
          onSuccess: () => {
            triggerToast(
              'success',
              t('deliverableSet.sendToReview.messages.success')
            )
            handleOpenChange(false)
          },
          onError: (error: unknown) => {
            const axiosError = error as AxiosError<{
              error?: { message?: string }
              message?: string
            }>
            const errorMessage =
              axiosError.response?.data?.error?.message ||
              axiosError.response?.data?.message ||
              axiosError.message ||
              t('deliverableSet.dialog.messages.errorGeneric')

            setErrorMsg(errorMessage)
          },
        }
      )
    },
    [
      handleOpenChange,
      inviteUsers,
      mutation,
      requestApprovalDeliverableSet?.id,
      requestApprovalDeliverableSet?.owner_id,
      t,
    ]
  )

  // Check if the submit button is disabled
  const isSubmitDisabled = useMemo(() => {
    return isSaving || !isValid || isMandatoryFieldsEmpty || !isHaveReviewer
  }, [isSaving, isValid, isMandatoryFieldsEmpty, isHaveReviewer])

  return (
    <Dialog open={showSendApprovalDialog} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="overflow-hidden flex flex-col max-h-[95dvh] sm:max-w-160">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {t('deliverableSet.sendToReview.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 px-6 -mx-6 overflow-x-hidden overflow-y-auto">
            <ErrorMessage message={errorMsg || ''} />

            <form
              id="send-to-review-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <InviteReviewerField />

              <div className="space-y-1 !mb-1">
                <label htmlFor="targetDate" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.targetDate')}
                </label>
                <Controller
                  name="targetDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      placeholder={t(
                        'deliverableSet.dialog.placeholders.selectTargetDate'
                      )}
                      value={field.value || undefined}
                      onDateChange={(date) => field.onChange(date || null)}
                      pickerIcon={<CalendarIcon className="size-4" />}
                      disabled={isSaving}
                      fullWidth
                      onlyFutureDate
                      allowInput
                    />
                  )}
                />
                {errors.targetDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.targetDate.message as unknown as string}
                  </p>
                )}
              </div>

              <NoteField
                control={control}
                name="note"
                disabled={isSaving}
                errors={errors}
                noteValue={noteWatch}
                isOptional
              />
            </form>
          </div>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleDraftApprovalRequest()}
              disabled={isSaving}
              className="rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-gray-light-700 mr-auto"
            >
              Save & Close
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
              className="rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-gray-light-700"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="send-to-review-form"
              className="rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
              variant="default"
              disabled={isSubmitDisabled}
            >
              {isSaving ? t('common.sending') : t('common.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default SendToReviewDialog
